import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './TreeIdentification.css';

// Import tree images
import hazel1 from '../assets/images/trees/hazel1.jpg';
import hazel2 from '../assets/images/trees/hazel2.jpg';
import hazel3 from '../assets/images/trees/hazel3.jpg';
import rowan1 from '../assets/images/trees/rowan1.jpg';
import rowan2 from '../assets/images/trees/rowan2.jpg';
import rowan3 from '../assets/images/trees/rowan3.jpg';
import rowan4 from '../assets/images/trees/rowan4.jpg';
import judasTree1 from '../assets/images/trees/judasTree1.jpg';
import judasTree2 from '../assets/images/trees/judasTree2.jpg';
import judasTree3 from '../assets/images/trees/judasTree3.jpg';
import beech1 from '../assets/images/trees/beech1.jpg';
import beech2 from '../assets/images/trees/beech2.jpg';
import beech3 from '../assets/images/trees/beech3.jpg';
import beech4 from '../assets/images/trees/beech4.jpg';
import wildCherry1 from '../assets/images/trees/wildCherry1.jpg';
import wildCherry2 from '../assets/images/trees/wildCherry2.webp';
import wildCherry3 from '../assets/images/trees/wildCherry3.jpg';
import wildCherry4 from '../assets/images/trees/wildCherry4.jpg';
import scotsPine1 from '../assets/images/trees/scotsPine1.jpg';
import scotsPine2 from '../assets/images/trees/scotsPine2.jpg';
import scotsPine3 from '../assets/images/trees/scotsPine3.jpg';
import scotsPine4 from '../assets/images/trees/scotsPine4.jpg';
import oakTree1 from '../assets/images/trees/oakTree1.jpg';
import oakTree2 from '../assets/images/trees/oakTree2.jpg';
import oakTree3 from '../assets/images/trees/oakTree3.jpg';
import oakTree4 from '../assets/images/trees/oakTree4.jpg';
import silverBirch1 from '../assets/images/trees/silverBirch1.jpg';
import silverBirch2 from '../assets/images/trees/silverBirch2.jpg';
import silverBirch3 from '../assets/images/trees/silverBirch3.jpg';
import hornbeam1 from '../assets/images/trees/hornbeam1.jpg';
import hornbeam2 from '../assets/images/trees/hornbeam2.jpg';
import hornbeam3 from '../assets/images/trees/hornbeam3.webp';
import hornbeam4 from '../assets/images/trees/hornbeam4.jpg';
import willow1 from '../assets/images/trees/willow1.jpg';
import willow2 from '../assets/images/trees/willow2.jpg';
import willow3 from '../assets/images/trees/willow3.jpg';
import elmTree1 from '../assets/images/trees/elmTree1.jpg';
import elmTree2 from '../assets/images/trees/elmTree2.jpg';
import elmTree3 from '../assets/images/trees/elmTree3.jpg';
import elmTree4 from '../assets/images/trees/elmTree4.jpg';
import limeTree1 from '../assets/images/trees/limeTree1.jpg';
import limeTree2 from '../assets/images/trees/limeTree2.jpg';
import limeTree3 from '../assets/images/trees/limeTree3.jpg';

interface TreeData {
  name: string;
  images: string[];
  lifespan: string;
  height: string;
  scientificName: string;
  insects: string;
  habitat: string;
  blooms: string;
  blossomColor: string;
  funFacts: string;
}

// Additional tree names for hard mode autocomplete
const additionalTrees = [
  'Ash', 'Maple', 'Sycamore', 'Chestnut', 'Poplar', 'Alder', 'Birch',
  'Cedar', 'Fir', 'Spruce', 'Larch', 'Yew', 'Holly', 'Laurel',
  'Magnolia', 'Walnut', 'Cherry', 'Apple', 'Pear', 'Plum',
  'Willow', 'Aspen', 'Elm', 'Lime', 'Beech', 'Oak', 'Hornbeam'
];

type DifficultyLevel = 'seed' | 'sapling' | 'tree';
type GameState = 'menu' | 'playing' | 'finished' | 'treeInfo' | 'reviewTrees';

const TreeIdentification: React.FC = () => {
  const { t } = useTranslation();
  const [gameState, setGameState] = useState<GameState>('menu');
  const [difficulty, setDifficulty] = useState<DifficultyLevel>('seed');
  const [currentTreeIndex, setCurrentTreeIndex] = useState(0);
  const [shuffledTrees, setShuffledTrees] = useState<TreeData[]>([]);
  const [score, setScore] = useState(0);
  const [userAnswer, setUserAnswer] = useState('');
  const [showAnswer, setShowAnswer] = useState(false);
  const [selectedTree, setSelectedTree] = useState<TreeData | null>(null);
  const [options, setOptions] = useState<TreeData[]>([]);
  const [autoCompleteOptions, setAutoCompleteOptions] = useState<string[]>([]);
  const [showAutoComplete, setShowAutoComplete] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [isAutoSlideshow, setIsAutoSlideshow] = useState(true);
  const [slideshowTimer, setSlideshowTimer] = useState<NodeJS.Timeout | null>(null);
  const [previousState, setPreviousState] = useState<GameState>('menu');
  const [hasPlayedGame, setHasPlayedGame] = useState(false);

  // Generate trees array from translations
  const trees: TreeData[] = [
    {
      name: t('treeIdentification.trees.hazel.name'),
      images: [hazel1, hazel2, hazel3],
      lifespan: t('treeIdentification.trees.hazel.lifespan'),
      height: t('treeIdentification.trees.hazel.height'),
      scientificName: t('treeIdentification.trees.hazel.scientificName'),
      insects: t('treeIdentification.trees.hazel.insects'),
      habitat: t('treeIdentification.trees.hazel.habitat'),
      blooms: t('treeIdentification.trees.hazel.blooms'),
      blossomColor: t('treeIdentification.trees.hazel.blossomColor'),
      funFacts: t('treeIdentification.trees.hazel.funFacts')
    },
    {
      name: t('treeIdentification.trees.rowan.name'),
      images: [rowan1, rowan2, rowan3, rowan4],
      lifespan: t('treeIdentification.trees.rowan.lifespan'),
      height: t('treeIdentification.trees.rowan.height'),
      scientificName: t('treeIdentification.trees.rowan.scientificName'),
      insects: t('treeIdentification.trees.rowan.insects'),
      habitat: t('treeIdentification.trees.rowan.habitat'),
      blooms: t('treeIdentification.trees.rowan.blooms'),
      blossomColor: t('treeIdentification.trees.rowan.blossomColor'),
      funFacts: t('treeIdentification.trees.rowan.funFacts')
    },
    {
      name: t('treeIdentification.trees.willow.name'),
      images: [willow1, willow2, willow3],
      lifespan: t('treeIdentification.trees.willow.lifespan'),
      height: t('treeIdentification.trees.willow.height'),
      scientificName: t('treeIdentification.trees.willow.scientificName'),
      insects: t('treeIdentification.trees.willow.insects'),
      habitat: t('treeIdentification.trees.willow.habitat'),
      blooms: t('treeIdentification.trees.willow.blooms'),
      blossomColor: t('treeIdentification.trees.willow.blossomColor'),
      funFacts: t('treeIdentification.trees.willow.funFacts')
    },
    {
      name: t('treeIdentification.trees.hornbeam.name'),
      images: [hornbeam1, hornbeam2, hornbeam3, hornbeam4],
      lifespan: t('treeIdentification.trees.hornbeam.lifespan'),
      height: t('treeIdentification.trees.hornbeam.height'),
      scientificName: t('treeIdentification.trees.hornbeam.scientificName'),
      insects: t('treeIdentification.trees.hornbeam.insects'),
      habitat: t('treeIdentification.trees.hornbeam.habitat'),
      blooms: t('treeIdentification.trees.hornbeam.blooms'),
      blossomColor: t('treeIdentification.trees.hornbeam.blossomColor'),
      funFacts: t('treeIdentification.trees.hornbeam.funFacts')
    },

          {
        name: t('treeIdentification.trees.judas.name'),
        images: [judasTree1, judasTree2, judasTree3],
        lifespan: t('treeIdentification.trees.judas.lifespan'),
        height: t('treeIdentification.trees.judas.height'),
        scientificName: t('treeIdentification.trees.judas.scientificName'),
        insects: t('treeIdentification.trees.judas.insects'),
        habitat: t('treeIdentification.trees.judas.habitat'),
        blooms: t('treeIdentification.trees.judas.blooms'),
        blossomColor: t('treeIdentification.trees.judas.blossomColor'),
        funFacts: t('treeIdentification.trees.judas.funFacts')
      },
          {
        name: t('treeIdentification.trees.beech.name'),
        images: [beech1, beech2, beech3, beech4],
        lifespan: t('treeIdentification.trees.beech.lifespan'),
        height: t('treeIdentification.trees.beech.height'),
        scientificName: t('treeIdentification.trees.beech.scientificName'),
        insects: t('treeIdentification.trees.beech.insects'),
        habitat: t('treeIdentification.trees.beech.habitat'),
        blooms: t('treeIdentification.trees.beech.blooms'),
        blossomColor: t('treeIdentification.trees.beech.blossomColor'),
        funFacts: t('treeIdentification.trees.beech.funFacts')
      },
          {
        name: t('treeIdentification.trees.wildCherry.name'),
        images: [wildCherry1, wildCherry2, wildCherry3, wildCherry4],
        lifespan: t('treeIdentification.trees.wildCherry.lifespan'),
        height: t('treeIdentification.trees.wildCherry.height'),
        scientificName: t('treeIdentification.trees.wildCherry.scientificName'),
        insects: t('treeIdentification.trees.wildCherry.insects'),
        habitat: t('treeIdentification.trees.wildCherry.habitat'),
        blooms: t('treeIdentification.trees.wildCherry.blooms'),
        blossomColor: t('treeIdentification.trees.wildCherry.blossomColor'),
        funFacts: t('treeIdentification.trees.wildCherry.funFacts')
      },
          {
        name: t('treeIdentification.trees.scotsPine.name'),
        images: [scotsPine1, scotsPine2, scotsPine3, scotsPine4],
        lifespan: t('treeIdentification.trees.scotsPine.lifespan'),
        height: t('treeIdentification.trees.scotsPine.height'),
        scientificName: t('treeIdentification.trees.scotsPine.scientificName'),
        insects: t('treeIdentification.trees.scotsPine.insects'),
        habitat: t('treeIdentification.trees.scotsPine.habitat'),
        blooms: t('treeIdentification.trees.scotsPine.blooms'),
        blossomColor: t('treeIdentification.trees.scotsPine.blossomColor'),
        funFacts: t('treeIdentification.trees.scotsPine.funFacts')
      },
          {
        name: t('treeIdentification.trees.oak.name'),
        images: [oakTree1, oakTree2, oakTree3, oakTree4],
        lifespan: t('treeIdentification.trees.oak.lifespan'),
        height: t('treeIdentification.trees.oak.height'),
        scientificName: t('treeIdentification.trees.oak.scientificName'),
        insects: t('treeIdentification.trees.oak.insects'),
        habitat: t('treeIdentification.trees.oak.habitat'),
        blooms: t('treeIdentification.trees.oak.blooms'),
        blossomColor: t('treeIdentification.trees.oak.blossomColor'),
        funFacts: t('treeIdentification.trees.oak.funFacts')
      },
          {
        name: t('treeIdentification.trees.silverBirch.name'),
        images: [silverBirch1, silverBirch2, silverBirch3],
        lifespan: t('treeIdentification.trees.silverBirch.lifespan'),
        height: t('treeIdentification.trees.silverBirch.height'),
        scientificName: t('treeIdentification.trees.silverBirch.scientificName'),
        insects: t('treeIdentification.trees.silverBirch.insects'),
        habitat: t('treeIdentification.trees.silverBirch.habitat'),
        blooms: t('treeIdentification.trees.silverBirch.blooms'),
        blossomColor: t('treeIdentification.trees.silverBirch.blossomColor'),
        funFacts: t('treeIdentification.trees.silverBirch.funFacts')
      },
    {
      name: t('treeIdentification.trees.elm.name'),
      images: [elmTree1, elmTree2, elmTree3, elmTree4],
      lifespan: t('treeIdentification.trees.elm.lifespan'),
      height: t('treeIdentification.trees.elm.height'),
      scientificName: t('treeIdentification.trees.elm.scientificName'),
      insects: t('treeIdentification.trees.elm.insects'),
      habitat: t('treeIdentification.trees.elm.habitat'),
      blooms: t('treeIdentification.trees.elm.blooms'),
      blossomColor: t('treeIdentification.trees.elm.blossomColor'),
      funFacts: t('treeIdentification.trees.elm.funFacts')
    },
    {
      name: t('treeIdentification.trees.lime.name'),
      images: [limeTree1, limeTree2, limeTree3],
      lifespan: t('treeIdentification.trees.lime.lifespan'),
      height: t('treeIdentification.trees.lime.height'),
      scientificName: t('treeIdentification.trees.lime.scientificName'),
      insects: t('treeIdentification.trees.lime.insects'),
      habitat: t('treeIdentification.trees.lime.habitat'),
      blooms: t('treeIdentification.trees.lime.blooms'),
      blossomColor: t('treeIdentification.trees.lime.blossomColor'),
      funFacts: t('treeIdentification.trees.lime.funFacts')
    }
  ];

  // Shuffle array utility
  const shuffleArray = <T,>(array: T[]): T[] => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Start game
  const startGame = (selectedDifficulty: DifficultyLevel) => {
    setDifficulty(selectedDifficulty);
    setGameState('playing');
    setCurrentTreeIndex(0);
    setScore(0);
    setUserAnswer('');
    setShowAnswer(false);
    setSelectedTree(null);
    setCurrentImageIndex(0);
    setHasPlayedGame(true); // Mark that user has played the game
    
    // Randomize tree order for the quiz
    const shuffled = shuffleArray(trees);
    setShuffledTrees(shuffled);
    
    // Setup options for current tree
    setupOptions(shuffled[0], selectedDifficulty);
  };

  // Setup options based on difficulty
  const setupOptions = (currentTree: TreeData, difficultyLevel: DifficultyLevel) => {
    if (difficultyLevel === 'seed') {
      // Easy: 3 options
      const otherTrees = trees.filter(tree => tree.name !== currentTree.name);
      const randomTrees = shuffleArray(otherTrees).slice(0, 2);
      const optionSet = shuffleArray([currentTree, ...randomTrees]);
      setOptions(optionSet);
    } else if (difficultyLevel === 'sapling') {
      // Medium: all 12 trees
      setOptions(shuffleArray(trees));
    }
    // Hard mode doesn't use options
  };

  // Handle answer submission
  const submitAnswer = (answer: string) => {
    const currentTree = shuffledTrees[currentTreeIndex];
    const isCorrect = answer.toLowerCase() === currentTree.name.toLowerCase();
    
    if (isCorrect) {
      setScore(score + 1);
    }
    
    setUserAnswer(answer);
    setShowAnswer(true);
  };

  // Move to next tree
  const nextTree = () => {
    if (currentTreeIndex < shuffledTrees.length - 1) {
      const nextIndex = currentTreeIndex + 1;
      setCurrentTreeIndex(nextIndex);
      setUserAnswer('');
      setShowAnswer(false);
      setAutoCompleteOptions([]);
      setShowAutoComplete(false);
      setCurrentImageIndex(0);
      setupOptions(shuffledTrees[nextIndex], difficulty);
    } else {
      setGameState('finished');
    }
  };

  // Handle autocomplete for hard mode
  const handleInputChange = (value: string) => {
    setUserAnswer(value);
    
    if (value.length > 1) {
      const allTreeNames = [...trees.map(t => t.name), ...additionalTrees];
      const matches = allTreeNames.filter(name => 
        name.toLowerCase().includes(value.toLowerCase())
      ).slice(0, 8);
      
      setAutoCompleteOptions(matches);
      setShowAutoComplete(matches.length > 0);
    } else {
      setShowAutoComplete(false);
    }
  };

  // Select autocomplete option
  const selectAutoComplete = (option: string) => {
    setUserAnswer(option);
    setShowAutoComplete(false);
  };

  // Show tree information
  const showTreeInfo = (tree: TreeData) => {
    setPreviousState(gameState);
    setSelectedTree(tree);
    setGameState('treeInfo');
  };

  // Show review trees page
  const showReviewTrees = () => {
    setGameState('reviewTrees');
  };

  // Reset game
  const resetGame = () => {
    setGameState('menu');
    setCurrentTreeIndex(0);
    setScore(0);
    setUserAnswer('');
    setShowAnswer(false);
    setSelectedTree(null);
    setCurrentImageIndex(0);
  };

  // Navigate tree images
  const nextImage = () => {
    const currentTree = shuffledTrees[currentTreeIndex];
    if (currentTree) {
      setCurrentImageIndex((prev) => (prev + 1) % currentTree.images.length);
    }
  };

  const prevImage = () => {
    const currentTree = shuffledTrees[currentTreeIndex];
    if (currentTree) {
      setCurrentImageIndex((prev) => (prev - 1 + currentTree.images.length) % currentTree.images.length);
    }
  };

  // Slideshow timer management
  const startSlideshow = () => {
    if (slideshowTimer) clearInterval(slideshowTimer);
    const timer = setInterval(() => {
      if (isAutoSlideshow) {
        nextImage();
      }
    }, 3000);
    setSlideshowTimer(timer);
  };

  const stopSlideshow = () => {
    if (slideshowTimer) {
      clearInterval(slideshowTimer);
      setSlideshowTimer(null);
    }
  };

  const pauseSlideshow = () => {
    setIsAutoSlideshow(false);
    stopSlideshow();
  };

  const currentTree = shuffledTrees[currentTreeIndex];

  // Auto-slideshow effect
  useEffect(() => {
    if (gameState === 'playing' && currentTree && isAutoSlideshow) {
      startSlideshow();
    } else {
      stopSlideshow();
    }

    return () => stopSlideshow();
  }, [gameState, currentTree, isAutoSlideshow, currentTreeIndex]);

  // Reset slideshow when tree changes
  useEffect(() => {
    setCurrentImageIndex(0);
    setIsAutoSlideshow(true);
  }, [currentTreeIndex]);

  // Touch/swipe handlers
  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    pauseSlideshow(); // Pause slideshow when user interacts
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      nextImage(); // Swipe left = next image
    } else if (isRightSwipe) {
      prevImage(); // Swipe right = previous image
    }
  };

  const progress = shuffledTrees.length > 0 ? ((currentTreeIndex + 1) / shuffledTrees.length) * 100 : 0;

  return (
    <div className="tree-identification-game">
      <div className="game-header">
        <h2>🌳 {t('treeIdentification.title')}</h2>
        <p>{t('treeIdentification.subtitle')}</p>
      </div>

      {gameState === 'menu' && (
        <div className="game-menu">
          <h3>{t('treeIdentification.chooseLevel')}</h3>
          <div className="difficulty-buttons">
            <button 
              className="difficulty-btn seed"
              onClick={() => startGame('seed')}
            >
              🌱 {t('treeIdentification.difficulties.seed.title')}
              <span className="difficulty-desc">{t('treeIdentification.difficulties.seed.description')}</span>
            </button>
            <button 
              className="difficulty-btn sapling"
              onClick={() => startGame('sapling')}
            >
              🌿 {t('treeIdentification.difficulties.sapling.title')}
              <span className="difficulty-desc">{t('treeIdentification.difficulties.sapling.description')}</span>
            </button>
            <button 
              className="difficulty-btn tree"
              onClick={() => startGame('tree')}
            >
              🌳 {t('treeIdentification.difficulties.tree.title')}
              <span className="difficulty-desc">{t('treeIdentification.difficulties.tree.description')}</span>
            </button>
          </div>
          
          <div className="menu-divider">
            <span>{t('treeIdentification.menu.or')}</span>
          </div>
          
          <div className="menu-review-section">
            <button 
              className="menu-review-btn"
              onClick={showReviewTrees}
            >
              📚 {t('treeIdentification.menu.reviewTrees')}
              <span className="difficulty-desc">{t('treeIdentification.menu.reviewTreesDesc')}</span>
            </button>
          </div>
        </div>
      )}

      {gameState === 'playing' && currentTree && (
        <div className="game-playing">
          <div className="game-progress">
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${progress}%` }}></div>
            </div>
            <span className="progress-text">
              {t('treeIdentification.game.tree')} {currentTreeIndex + 1} {t('treeIdentification.game.of')} {shuffledTrees.length} | {t('treeIdentification.game.score')}: {score}
            </span>
          </div>

          <div className="image-section">
            <div className="tree-image-container">
              {currentTree.images[currentImageIndex] && currentTree.images[currentImageIndex].includes('/static/media/') ? (
                <img 
                  src={currentTree.images[currentImageIndex]} 
                  alt={`${currentTree.name} - ${t('treeIdentification.game.tree')} ${currentImageIndex + 1}`}
                  className="tree-image"
                  onTouchStart={onTouchStart}
                  onTouchMove={onTouchMove}
                  onTouchEnd={onTouchEnd}
                  onError={(e) => {
                    console.error('Image failed to load:', currentTree.images[currentImageIndex]);
                    e.currentTarget.style.display = 'none';
                  }}
                />
              ) : (
                <div className="image-placeholder">
                  <span>🌳</span>
                  <p className="error-message">{t('treeIdentification.game.imageComingSoon')}</p>
                </div>
              )}
              <div className="image-controls">
                <button onClick={() => { pauseSlideshow(); prevImage(); }} className="image-nav-btn">
                  {t('treeIdentification.game.previousImage')}
                </button>
                <div className="slideshow-dots">
                  {currentTree.images.map((_, index) => (
                    <button
                      key={index}
                      className={`dot ${index === currentImageIndex ? 'active' : ''}`}
                      onClick={() => { 
                        pauseSlideshow(); 
                        setCurrentImageIndex(index); 
                      }}
                      aria-label={`View photo ${index + 1}`}
                    />
                  ))}
                </div>
                <button onClick={() => { pauseSlideshow(); nextImage(); }} className="image-nav-btn">
                  {t('treeIdentification.game.nextImage')}
                </button>
              </div>
            </div>
          </div>

          {!showAnswer && (
            <div className="answer-section">
              {difficulty === 'tree' ? (
                // Hard mode: text input with autocomplete
                <div className="text-input-section">
                  <h3>{t('treeIdentification.game.typeQuestion')}</h3>
                  <div className="autocomplete-container">
                    <input
                      type="text"
                      value={userAnswer}
                      onChange={(e) => handleInputChange(e.target.value)}
                      placeholder={t('treeIdentification.game.placeholder')}
                      className="tree-input"
                    />
                    {showAutoComplete && (
                      <div className="autocomplete-dropdown">
                        {autoCompleteOptions.map((option, index) => (
                          <div
                            key={index}
                            className="autocomplete-option"
                            onClick={() => selectAutoComplete(option)}
                          >
                            {option}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <button
                    className="submit-btn"
                    onClick={() => submitAnswer(userAnswer)}
                    disabled={!userAnswer.trim()}
                  >
                    {t('treeIdentification.game.submitAnswer')}
                  </button>
                </div>
              ) : (
                // Easy and Medium modes: multiple choice
                <div className="multiple-choice">
                  <h3>{t('treeIdentification.game.question')}</h3>
                  <div className="options-grid">
                    {options.map((option, index) => (
                      <button
                        key={index}
                        className="option-btn"
                        onClick={() => submitAnswer(option.name)}
                      >
                        {option.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {showAnswer && (
            <div className="answer-result">
              <div className={`result-message ${
                userAnswer.toLowerCase() === currentTree.name.toLowerCase() ? 'correct' : 'incorrect'
              }`}>
                {userAnswer.toLowerCase() === currentTree.name.toLowerCase() ? (
                  <span>✅ {t('treeIdentification.game.correct')} {currentTree.name}</span>
                ) : (
                  <span>❌ {t('treeIdentification.game.incorrect')} {currentTree.name}</span>
                )}
              </div>
              
              {difficulty === 'seed' && userAnswer.toLowerCase() !== currentTree.name.toLowerCase() && (
                <div className="correct-highlight">
                  <div className="options-grid">
                    {options.map((option, index) => (
                      <button
                        key={index}
                        className={`option-btn ${option.name === currentTree.name ? 'correct-answer' : 'incorrect-option'}`}
                        disabled
                      >
                        {option.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="answer-buttons">
                <button 
                  className="tree-info-btn"
                  onClick={() => showTreeInfo(currentTree)}
                >
                  {t('treeIdentification.game.learnMore')} {currentTree.name}
                </button>
                <button 
                  className="continue-btn"
                  onClick={nextTree}
                >
                  {currentTreeIndex < shuffledTrees.length - 1 ? t('treeIdentification.game.nextTree') : t('treeIdentification.game.finishGame')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {gameState === 'finished' && (
        <div className="game-finished">
          <h3>🎉 {t('treeIdentification.gameComplete.title')}</h3>
          <div className="final-score">
            <span className="score-text">{t('treeIdentification.gameComplete.yourScore')}: {score} {t('treeIdentification.gameComplete.outOf')} {shuffledTrees.length}</span>
            <div className="score-percentage">
              {Math.round((score / shuffledTrees.length) * 100)}%
            </div>
          </div>
          
          <div className="score-message">
            {score === shuffledTrees.length && "🏆 " + t('treeIdentification.gameComplete.messages.perfect')}
            {score >= shuffledTrees.length * 0.8 && score < shuffledTrees.length && "🌳 " + t('treeIdentification.gameComplete.messages.excellent')}
            {score >= shuffledTrees.length * 0.6 && score < shuffledTrees.length * 0.8 && "👍 " + t('treeIdentification.gameComplete.messages.good')}
            {score < shuffledTrees.length * 0.6 && "🌱 " + t('treeIdentification.gameComplete.messages.start')}
          </div>

          <div className="final-buttons">
            <button className="play-again-btn" onClick={resetGame}>
              {t('treeIdentification.gameComplete.playAgain')}
            </button>
            <button className="review-trees-btn" onClick={showReviewTrees}>
              {t('treeIdentification.gameComplete.reviewTrees')}
            </button>
          </div>
        </div>
      )}

      {gameState === 'reviewTrees' && (
        <div className="review-trees">
          <div className="review-header">
            <h3>🌳 {t('treeIdentification.reviewTrees.title')}</h3>
            <p>{t('treeIdentification.reviewTrees.subtitle')}</p>
          </div>
          
          <div className="trees-grid">
            {trees.map((tree, index) => (
              <button
                key={index}
                className="tree-review-btn"
                onClick={() => showTreeInfo(tree)}
              >
                <div className="tree-image-preview">
                  {tree.images[2] && tree.images[2].includes('/static/media/') ? (
                    <img 
                      src={tree.images[2]} 
                      alt={tree.name}
                      className="tree-preview-image"
                    />
                  ) : (
                    <div className="tree-preview-placeholder">
                      <span>🌳</span>
                    </div>
                  )}
                </div>
                <span className="tree-name">{tree.name}</span>
              </button>
            ))}
          </div>
          
          <div className="review-buttons">
            <button className="back-to-menu-btn" onClick={resetGame}>
              {t('treeIdentification.reviewTrees.backToMenu')}
            </button>
            <button className="play-again-btn" onClick={resetGame}>
              {hasPlayedGame ? t('treeIdentification.gameComplete.playAgain') : t('treeIdentification.menu.playGame')}
            </button>
          </div>
        </div>
      )}

      {gameState === 'treeInfo' && selectedTree && (
        <div className="tree-info">
          <div className="tree-info-header">
            <h3>🌳 {selectedTree.name}</h3>
            <button className="close-btn" onClick={() => setGameState(previousState)}>
              ✕
            </button>
          </div>
          
          <div className="tree-details">
            <div className="tree-detail">
              <strong>{t('treeIdentification.treeInfo.scientificName')}:</strong> <em>{selectedTree.scientificName}</em>
            </div>
            <div className="tree-detail">
              <strong>{t('treeIdentification.treeInfo.lifespan')}:</strong> {selectedTree.lifespan}
            </div>
            <div className="tree-detail">
              <strong>{t('treeIdentification.treeInfo.height')}:</strong> {selectedTree.height}
            </div>
            <div className="tree-detail">
              <strong>{t('treeIdentification.treeInfo.habitat')}:</strong> {selectedTree.habitat}
            </div>
            <div className="tree-detail">
              <strong>{t('treeIdentification.treeInfo.blooms')}:</strong> {selectedTree.blooms}
            </div>
            <div className="tree-detail">
              <strong>{t('treeIdentification.treeInfo.blossomColor')}:</strong> {selectedTree.blossomColor}
            </div>
            <div className="tree-detail">
              <strong>{t('treeIdentification.treeInfo.insects')}:</strong> {selectedTree.insects}
            </div>
            <div className="tree-detail">
              <strong>{t('treeIdentification.treeInfo.funFacts')}:</strong> {selectedTree.funFacts}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TreeIdentification; 